import { Component, computed, DestroyRef, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Subject, forkJoin, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import {
  YdsButtonComponent,
  YdsCardComponent,
  YdsDropdownComponent,
  YdsEmptyStatesComponent,
  YdsSpinnerComponent,
  YdsTableComponent,
  YdsSplitButtonComponent,
  YdsTableCellDirective,
  YdsPaginationComponent,
  type YdsDropdownOption,
  type YdsTableColumn,
  type YdsSplitButtonItem,
} from '../../../yds-angular';
import { BookingRule } from '../../../model/booking-rule.model';
import { BookingGroup } from '../../../model/booking-group.model';
import { BookingRuleService } from '../bookingrule.service';
import { BookingGroupService } from '../../bookinggroup/bookinggroup.service';
import { BookingDefinitionService } from '../../bookingdefinition/bookingdefinition.service';
import { GeneralLedgerAccountService } from '../../generalledgeraccount/generalledgeraccount.service';
import { ConfirmDialogService } from '../../../dialog/confirm-dialog/confirm-dialog.service';
import { deleteBookingRuleWithChecks } from '../bookingrule-shared';

/** Account details joined onto a booking rule for display. */
interface AccountInfo {
  number: string;
  ledgerName: string;
}

@Component({
  selector: 'app-bookingrule-list',
  imports: [
    YdsCardComponent,
    YdsButtonComponent,
    YdsDropdownComponent,
    YdsTableComponent,
    YdsSpinnerComponent,
    YdsEmptyStatesComponent,
    YdsSplitButtonComponent,
    YdsTableCellDirective,
    YdsPaginationComponent,
  ],
  templateUrl: './bookingrule-list.component.html',
  styleUrl: './bookingrule-list.component.scss',
})
export class BookingruleListComponent implements OnInit {
  readonly pageSize = 10;
  readonly tableColumns: YdsTableColumn[] = [
    { key: 'bookingDefinition_name', header: 'Boekingsdefinitie' },
    // Long single-word header; give it room so the fixed table layout doesn't break it mid-word.
    { key: 'generalLedgerAccountNumber', header: 'Grootboekrekeningnummer', width: '210px' },
    { key: 'generalLedgerName', header: 'Grootboek' },
    { key: 'kindOfBooking', header: 'Soort rekening' },
    { key: 'actief', header: 'Actief' },
    { key: 'actions', header: 'Actieknop', width: '160px' },
  ];

  readonly splitButtonItems: readonly YdsSplitButtonItem[] = [
    { id: 'edit', label: 'Wijzigen' },
    { id: 'bookinggroup', label: 'Boekingsgroep' },
    { id: 'bookingdefinition', label: 'Boekingsdefinitie' },
    { id: 'copy', label: 'Kopiëren', disabled: true },
    { id: 'delete', label: 'Verwijderen' },
  ];

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private bookingRuleService = inject(BookingRuleService);
  private bookingGroupService = inject(BookingGroupService);
  private bookingDefinitionService = inject(BookingDefinitionService);
  private glAccountService = inject(GeneralLedgerAccountService);
  private dialogService = inject(ConfirmDialogService);
  private destroyRef = inject(DestroyRef);

  bookingGroups: WritableSignal<BookingGroup[]> = signal([]);
  bookingGroupId = signal<string>('');
  isLoadingGroups = signal(true);
  bookingRules: WritableSignal<BookingRule[]> = signal([]);
  pageIndex = signal(0);
  isLoading = signal(true);

  /** GL account info keyed by general ledger account id, used to join display columns. */
  private readonly accountInfoById = signal<Map<string, AccountInfo>>(new Map());
  /** Booking definition active indicator keyed by booking definition id. */
  private readonly definitionActiveById = signal<Map<string, boolean>>(new Map());

  bookingGroupOptions = computed<YdsDropdownOption[]>(() =>
    this.bookingGroups().map((group) => ({
      id: group.bookingGroupId,
      label: group.bookingGroup_name,
    })),
  );

  private reload$ = new Subject<void>();

  pagedRules = computed(() => {
    const start = this.pageIndex() * this.pageSize;
    return this.bookingRules().slice(start, start + this.pageSize);
  });

  tableItems = computed(() => {
    const accounts = this.accountInfoById();
    const definitions = this.definitionActiveById();
    return this.pagedRules().map((item) => {
      const account = accounts.get(item.generalLedgerAccountId);
      return {
        id: item.bookingRuleId,
        bookingDefinition_name: item.bookingDefinitionName ?? '',
        generalLedgerAccountNumber: account?.number ?? '',
        generalLedgerName: account?.ledgerName ?? '',
        kindOfBooking: this.formatKindOfBooking(item.kindOfBooking),
        actief: this.formatActive(definitions.get(item.bookingDefinitionId)),
      };
    });
  });

  private formatKindOfBooking(kind: string | null | undefined): string {
    if (kind === 'DEBIT') {
      return 'Debet';
    }
    if (kind === 'CREDIT') {
      return 'Credit';
    }
    return '';
  }

  private formatActive(active: boolean | undefined): string {
    if (active === undefined) {
      return '';
    }
    return active ? 'Ja' : 'Nee';
  }

  constructor() {
    this.reload$
      .pipe(
        switchMap(() => {
          this.isLoading.set(true);
          this.bookingRules.set([]);
          this.accountInfoById.set(new Map());
          this.definitionActiveById.set(new Map());
          const groupId = this.bookingGroupId();
          if (!groupId) {
            this.isLoading.set(false);
            return EMPTY;
          }
          // Load the rules plus the data needed to join the display columns:
          // booking definitions (Actief) and general ledger accounts
          // (Grootboekrekeningnummer, Grootboek, Soort rekening).
          return forkJoin({
            rules: this.bookingRuleService.getByContext(groupId).pipe(catchError(() => of([]))),
            definitions: this.bookingDefinitionService
              .getByContext(groupId)
              .pipe(catchError(() => of([]))),
            accounts: this.bookingGroupService.getBookingGroupById(groupId).pipe(
              switchMap((group) =>
                group ? this.glAccountService.getByContext(group.generalLedgerId) : of([]),
              ),
              catchError(() => of([])),
            ),
          }).pipe(
            catchError(() => {
              this.isLoading.set(false);
              return EMPTY;
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ rules, definitions, accounts }) => {
          this.accountInfoById.set(
            new Map(
              accounts.map((account) => [
                account.generalLedgerAccountId,
                {
                  number: account.generalLedger_Account,
                  ledgerName: account.generalLedger_Name,
                },
              ]),
            ),
          );
          this.definitionActiveById.set(
            new Map(definitions.map((def) => [def.bookingDefinitionId, def.activeIndicator])),
          );
          this.bookingRules.set(rules);
          this.pageIndex.set(0);
          this.isLoading.set(false);
        },
      });
  }

  ngOnInit(): void {
    this.loadBookingGroups();
  }

  loadBookingGroups(): void {
    this.isLoadingGroups.set(true);
    this.bookingGroupService
      .getByContext()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (groups) => {
          this.bookingGroups.set(groups);
          this.isLoadingGroups.set(false);
          if (groups.length > 0) {
            // Select the booking group from the route when present, otherwise the first one.
            const routeGroupId = this.route.snapshot.paramMap.get('bookingGroupId');
            const selected = groups.find((group) => group.bookingGroupId === routeGroupId);
            this.onBookingGroupChange((selected ?? groups[0]).bookingGroupId);
          } else {
            this.bookingGroupId.set('');
            this.isLoading.set(false);
          }
        },
        error: () => {
          this.bookingGroups.set([]);
          this.isLoadingGroups.set(false);
          this.isLoading.set(false);
        },
      });
  }

  onBookingGroupChange(bookingGroupId: string | null): void {
    this.bookingGroupId.set(bookingGroupId ?? '');
    this.loadBookingRules();
  }

  loadBookingRules(): void {
    this.reload$.next();
  }

  navigateToCreate(): void {
    this.router.navigate(['/config/bookingrule/edit'], {
      state: { bookingGroupId: this.bookingGroupId() },
    });
  }

  onTableRowClick(row: Record<string, unknown>): void {
    // Default action when clicking a row is "Bekijk details".
    this.viewItem(String(row['id']));
  }

  viewItem(id: string, event?: MouseEvent): void {
    event?.stopPropagation();
    this.router.navigate(['/config/bookingrule/edit', id]);
  }

  handleActionClick(id: string, actionId: string): void {
    if (actionId === 'edit') {
      this.viewItem(id);
      return;
    }
    if (actionId === 'bookinggroup') {
      this.router.navigate(['/config/bookinggroup/editable', this.bookingGroupId()]);
      return;
    }
    if (actionId === 'bookingdefinition') {
      this.router.navigate(['/config/bookingdefinition', this.bookingGroupId()]);
      return;
    }
    if (actionId === 'delete') {
      this.deleteItem(id);
      return;
    }
  }

  private deleteItem(id: string): void {
    deleteBookingRuleWithChecks({
      id,
      service: this.bookingRuleService,
      dialogService: this.dialogService,
      destroyRef: this.destroyRef,
      onDeleted: () => this.loadBookingRules(),
    });
  }
}
